/**
 * Aggregate a print order's queue into unique IDs (one row per storage_id)
 * and compute "IDs after the scanned one" for print-event handling.
 */

export const PRINT_EVENT_HANDLING_KINDS = ['warehouse', 'big'] as const;

export const PRINT_EVENT_LABEL_NOT_IN_JOB = 'Label not included in print job';
export const PRINT_EVENT_NO_MISSING_TO_DELETE = "No missing ID's to delete";
export const PRINT_EVENT_NO_MISSING_TO_REPRINT = "No missing ID's to reprint";
export const PRINT_EVENT_NO_IDS_TO_DELETE = 'No storage IDs on this print order';
export const PRINT_EVENT_NO_LABELS_CONFIRM =
  'Click OK to delete the IDs of labels that didn\'t print, and attempt to print again.';

export interface PrintJobIdRow {
  readonly storageId: string;
  readonly prefix: string;
  readonly count: number;
  readonly sku: string | null;
  readonly shelfIndex: string | null;
  readonly baseQty: number | null;
}

export interface PrintJobIdSourceItem {
  readonly seq: number;
  readonly storageId: string | null;
  readonly storageIdPrefix: string | null;
  readonly sku: string | null;
  readonly shelfIndex: string | null;
  readonly baseQty: number | null;
}

interface AggregatedRow extends PrintJobIdRow {
  readonly firstSeq: number;
}

function normalizePrefix(value: string): string {
  return value.trim().toLowerCase();
}

/** Collapse queue items into unique IDs, ordered by the first seq of each storage_id. */
export function aggregatePrintJobIds(items: readonly PrintJobIdSourceItem[]): PrintJobIdRow[] {
  const byStorageId = new Map<string, AggregatedRow>();

  for (const item of items) {
    const storageId = (item.storageId ?? '').trim();
    if (!storageId) continue;

    const existing = byStorageId.get(storageId);
    if (existing) {
      byStorageId.set(storageId, { ...existing, count: existing.count + 1 });
      continue;
    }

    const prefix = normalizePrefix(item.storageIdPrefix ?? storageId.substring(0, 6));
    byStorageId.set(storageId, {
      storageId,
      prefix,
      count: 1,
      sku: item.sku,
      shelfIndex: item.shelfIndex,
      baseQty: item.baseQty,
      firstSeq: item.seq,
    });
  }

  return [...byStorageId.values()]
    .sort((left, right) => left.firstSeq - right.firstSeq)
    .map(({ firstSeq: _firstSeq, ...row }) => row);
}

export function flattenPrintOrderItems(
  sheets: readonly { readonly items: readonly PrintJobIdSourceItem[] }[],
): PrintJobIdSourceItem[] {
  return sheets.flatMap((sheet) => [...sheet.items]);
}

export function findPrintJobId(
  rows: readonly PrintJobIdRow[],
  scannedPrefix: string,
): PrintJobIdRow | null {
  const prefix = normalizePrefix(scannedPrefix);
  return rows.find((row) => row.prefix === prefix) ?? null;
}

/** Unique IDs whose first seq is after the scanned ID. Empty when scanned is last or unknown. */
export function idsAfter(rows: readonly PrintJobIdRow[], scannedPrefix: string): PrintJobIdRow[] {
  const prefix = normalizePrefix(scannedPrefix);
  const index = rows.findIndex((row) => row.prefix === prefix);
  if (index < 0) return [];
  return rows.slice(index + 1);
}

export function formatPrintJobBannerCounts(idCount: number, labelCount: number): string {
  const idWord = idCount === 1 ? 'ID' : 'IDs';
  const labelWord = labelCount === 1 ? 'Label' : 'Labels';
  return `${idCount} ${idWord}, ${labelCount} ${labelWord}`;
}

export function isPrintEventHandlingKind(kind: string): boolean {
  return kind === 'warehouse' || kind === 'big';
}

export type PrintJobIdRowTone = 'earlier' | 'scanned' | 'later';

/** Row tone after a last-label scan: earlier (printed) / scanned / later. Unknown prefix is null. */
export function printJobIdRowTone(
  rows: readonly PrintJobIdRow[],
  scannedPrefix: string,
  rowPrefix: string,
): PrintJobIdRowTone | null {
  const scanned = normalizePrefix(scannedPrefix);
  const prefix = normalizePrefix(rowPrefix);
  const scannedIndex = rows.findIndex((row) => row.prefix === scanned);
  const rowIndex = rows.findIndex((row) => row.prefix === prefix);
  if (scannedIndex < 0 || rowIndex < 0) return null;
  if (rowIndex < scannedIndex) return 'earlier';
  if (rowIndex === scannedIndex) return 'scanned';
  return 'later';
}
